'use client';

import * as flags from 'country-flag-icons/react/3x2';

import { useRef, useState, type ComponentProps, type JSX } from 'react';

import { Text } from '~/components/text';
import { Show } from '~/components/show';
import { Button } from '~/components/button';
import { cn, getCountries, getCountryCode } from '~/utils';
import { CaretDownIcon, CheckIcon } from '~/components/icons';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/popover';
import {
  Command,
  CommandItem,
  CommandList,
  CommandGroup,
  CommandInput,
} from '~/components/command';

interface ISelectCountryProps {
  value?: string;
  placeholder?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
}

export function SelectCountry({
  value,
  onChange,
  defaultValue,
  placeholder = 'Select a country',
  ...rest
}: ISelectCountryProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLButtonElement>(null);
  const [selected, setSelected] = useState(value || defaultValue);

  const handleSelectCountry: ComponentProps<typeof CommandItem>['onSelect'] = (
    country,
  ) => {
    setSelected(country);
    onChange?.(country);
  };

  const SelectedFlag =
    flags[getCountryCode(selected || '') as keyof typeof flags];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          {...rest}
          role='combobox'
          variant='outline'
          ref={containerRef}
          aria-expanded={open}
          className={cn(
            'w-full justify-between border-[#ececec] px-4 py-2 min-h-[46px] h-[unset]',
            'hover:border-[#cdcdcd] hover:bg-transparent active:bg-transparent',
            open && 'border-blueBonnet-500 hover:border-blueBonnet-500',
          )}
        >
          <div className='flex flex-wrap gap-2 items-center min-h-[30px]'>
            <Show when={!selected}>
              <Text className='text-[#bfbfbf]'>{placeholder}</Text>
            </Show>
            <Show when={SelectedFlag}>
              <SelectedFlag className='!w-[40px] !h-[20px]' />
            </Show>
            <Text className='text-[16px] leading-[130%]'>{selected}</Text>
          </div>
          <CaretDownIcon className='size-4' />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align='start'
        side='bottom'
        sideOffset={8}
        className='p-0 w-full'
        style={{
          width: containerRef.current
            ? `${containerRef.current.offsetWidth}px`
            : '100%',
        }}
      >
        <Command className='w-full'>
          <CommandInput
            placeholder='Search country...'
            wrapperClassName='py-3 px-4 h-[unset]'
            className='p-0 h-[unset] text-(length:--text-md) leading-[130%]'
          />
          <CommandList>
            <CommandGroup className='p-0'>
              {Object.entries(getCountries()).map(([code, name]) => {
                const isSelected = selected === name;
                const Flag = flags[code as keyof typeof flags];

                return (
                  <CommandItem
                    key={name}
                    value={name}
                    onSelect={handleSelectCountry}
                    className={cn(
                      'flex items-center py-3 px-4 gap-2',
                      isSelected && 'bg-accent',
                    )}
                  >
                    <Flag className='!w-[40px] !h-[20px]' />
                    <Text className='text-[16px] flex-1'>{name}</Text>
                    <Show when={isSelected}>
                      <CheckIcon variant='stroke' />
                    </Show>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
